/**
 * /shadow — Iter122.7 §2 v1↔v2 diagnostics + certification workspace
 * ===================================================================
 * Access-controlled dedicated page for internal testers.  Displays
 * v1 and v2 side by side for the same symbol with a classified
 * field-level drift block, chain-hash reconciliation, and a
 * NON-DETERMINISTIC label when source inputs differ.
 *
 * Also surfaces:
 *   - collector health + metrics (raw attempts, records persisted,
 *     duplicates suppressed, collector errors)
 *   - validation-window status (start_at, target_end_at, versions)
 *   - reason-code renderer for the v2 payload
 *   - Return-to-v1 action + shadow-mode banner
 *
 * If the user is not eligible or not opted in, the diagnostics
 * request returns 403 and the page shows a friendly gate message.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import {
  ShadowBanner, ShadowFallbackBanner, ReasonCodeList, useShadowStatus,
} from "@/components/gex/ReasonCodeRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANONICAL_TICKERS = ["SPY", "QQQ", "AAPL", "NVDA", "NCLH", "RITM", "AMPH"];

// Iter122.7.1 §4 — Recommended production ticker universe. Displayed
// on the Diagnostic Sweep panel so operators can confirm what will
// be swept without discovering it after they click.
const RECOMMENDED_TICKERS = [
  "SPY", "QQQ", "AAPL", "TSLA", "NVDA",
  "RITM", "NCLH", "F", "NU", "INTC",
  "AMPH",
];


function fmtNumber(v, digits = 4) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v !== "number") return String(v);
  if (Math.abs(v) >= 1e6) return v.toExponential(3);
  return Number(v).toFixed(digits);
}


function PairRow({ label, v1, v2, testid }) {
  return (
    <tr data-testid={testid || `pair-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <td style={{ color: "#8A8A93", padding: "6px 10px",
                     fontSize: "11px", textTransform: "uppercase",
                     letterSpacing: "0.08em", fontWeight: 700 }}>{label}</td>
      <td style={{ padding: "6px 10px", color: "#e5e7eb",
                     fontFamily: "ui-monospace", fontSize: "12px" }}
           data-testid={`v1-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        {typeof v1 === "number" ? fmtNumber(v1) : (v1 ?? "—")}
      </td>
      <td style={{ padding: "6px 10px", color: "#e5e7eb",
                     fontFamily: "ui-monospace", fontSize: "12px" }}
           data-testid={`v2-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        {typeof v2 === "number" ? fmtNumber(v2) : (v2 ?? "—")}
      </td>
    </tr>
  );
}


function DriftTable({ drift, hashesMatch, comparisonLabel }) {
  return (
    <div data-testid="drift-table">
      <div style={{ display: "flex", alignItems: "center", gap: "10px",
                     marginBottom: "8px" }}>
        <h3 style={{ margin: 0, color: "#fff", fontSize: "13px",
                       textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Field-level Drift
        </h3>
        <span
          data-testid="comparison-label"
          style={{
            padding: "3px 8px", fontSize: "10px", fontWeight: 700,
            letterSpacing: "0.05em", textTransform: "uppercase",
            border: `1px solid ${hashesMatch ? "#00FF8880" : "#FFB80080"}`,
            color:  hashesMatch ? "#00FF88" : "#FFB800",
            borderRadius: "3px",
          }}
        >
          {comparisonLabel}
        </span>
      </div>
      {!hashesMatch && (
        <div data-testid="hash-mismatch-warning"
              style={{ background: "#3f2f0e", color: "#ffd18a",
                       padding: "8px 12px", fontSize: "11px",
                       border: "1px solid rgba(255,209,138,0.25)",
                       borderRadius: "3px", marginBottom: "8px" }}>
          v1 and v2 chain hashes differ. Field-level drift below reflects
          differences in the underlying chain fetch, not engine math.
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse",
                       background: "#0A0A0D", border: "1px solid #26262B" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "6px 10px",
                            color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase" }}>Field</th>
            <th style={{ textAlign: "left", padding: "6px 10px",
                            color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase" }}>v1</th>
            <th style={{ textAlign: "left", padding: "6px 10px",
                            color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase" }}>v2</th>
            <th style={{ textAlign: "left", padding: "6px 10px",
                            color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase" }}>Δ</th>
            <th style={{ textAlign: "left", padding: "6px 10px",
                            color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase" }}>Class</th>
          </tr>
        </thead>
        <tbody>
          {drift.map((row, i) => (
            <tr key={row.field + "-" + i}
                 data-testid={`drift-row-${row.field}`}
                 style={{ borderTop: "1px solid #1A1A20" }}>
              <td style={{ padding: "6px 10px", color: "#fff",
                              fontSize: "11px", fontWeight: 600 }}>
                {row.field}
              </td>
              <td style={{ padding: "6px 10px", color: "#e5e7eb",
                              fontFamily: "ui-monospace", fontSize: "11px" }}>
                {typeof row.v1 === "number" ? fmtNumber(row.v1)
                                              : (row.v1 ?? "—")}
              </td>
              <td style={{ padding: "6px 10px", color: "#e5e7eb",
                              fontFamily: "ui-monospace", fontSize: "11px" }}>
                {typeof row.v2 === "number" ? fmtNumber(row.v2)
                                              : (row.v2 ?? "—")}
              </td>
              <td style={{ padding: "6px 10px", color: "#e5e7eb",
                              fontFamily: "ui-monospace", fontSize: "11px" }}>
                {typeof row.delta === "number" ? fmtNumber(row.delta) : "—"}
              </td>
              <td style={{ padding: "6px 10px", fontSize: "10px",
                              color: row.classification === "preserved"
                                       ? "#00FF88"
                                       : (row.classification.startsWith("expected") ? "#FFB800" : "#fca5a5") }}>
                {row.classification}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function CollectorHealthPanel() {
  const [health, setHealth] = useState(null);
  const [ticking, setTicking] = useState(false);
  const [tickResult, setTickResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/forge/gex/v2/collector/health`);
      setHealth(r.data);
    } catch { setHealth(null); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const runManualTick = async () => {
    setTicking(true); setTickResult(null);
    try {
      const r = await axios.post(`${API}/forge/gex/v2/collector/tick-now`,
                                    { force: true });
      setTickResult(r.data);
      await refresh();
    } catch (e) {
      setTickResult({ error: e?.response?.data?.detail || String(e) });
    } finally {
      setTicking(false);
    }
  };

  if (!health) return null;
  const m = health.metrics_since_process_start || {};
  const rt = health.runtime || {};
  const ms = rt.market_session_now || {};
  return (
    <div data-testid="collector-health-panel"
          style={{ border: "1px solid #26262B", background: "#121216",
                   borderRadius: "4px", padding: "14px 16px",
                   display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center", gap: "10px" }}>
        <h3 style={{ margin: 0, color: "#fff", fontSize: "12px",
                       textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Collector Health
        </h3>
        <button data-testid="collector-tick-now-btn"
                 onClick={runManualTick} disabled={ticking}
                 title="Run one controlled collector cycle (writes to gex_v2_forward_validation)"
                 style={{ background: "transparent", color: "#FFB800",
                          border: "1px solid #FFB80080", borderRadius: "3px",
                          padding: "3px 8px", fontSize: "10px",
                          fontWeight: 700, cursor: ticking ? "wait" : "pointer",
                          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {ticking ? "Ticking…" : "Tick Now"}
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                     gap: "8px", fontSize: "11px", color: "#8A8A93" }}>
        <div>Enabled: <span style={{ color: health.collector_enabled ? "#00FF88" : "#8A8A93" }}
                                data-testid="collector-enabled">
          {String(!!health.collector_enabled)}
        </span></div>
        <div>Cadence (s): <span style={{ color: "#e5e7eb" }}>{health.cadence_sec}</span></div>
        <div>Dedup TTL (s): <span style={{ color: "#e5e7eb" }}>{health.dedup_ttl_sec}</span></div>
        <div>Method version: <span style={{ color: "#e5e7eb",
                                                fontFamily: "ui-monospace" }}>
          {health.collection_method_version}
        </span></div>
        <div>Snapshots total: <span style={{ color: "#e5e7eb" }}
                                          data-testid="collector-total-snapshots">
          {health.total_signal_snapshots}
        </span></div>
        <div>Enrichments total: <span style={{ color: "#e5e7eb" }}>
          {health.total_outcome_enrichments}
        </span></div>
      </div>

      {/* Iter122.7.1a — Runtime scheduler block */}
      <div style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px" }}
            data-testid="collector-runtime-block">
        <div style={{ display: "flex", alignItems: "center", gap: "8px",
                        marginBottom: "4px" }}>
          <span style={{ color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Scheduler runtime
          </span>
          <span data-testid="scheduler-running-badge"
                 style={{ fontSize: "9px", padding: "1px 6px",
                          borderRadius: "3px", fontWeight: 700,
                          letterSpacing: "0.05em",
                          color: rt.scheduler_running ? "#00FF88" : "#fca5a5",
                          border: `1px solid ${rt.scheduler_running ? "#00FF8880" : "#fca5a580"}` }}>
            {rt.scheduler_running ? "RUNNING" : "STOPPED"}
          </span>
          <span data-testid="market-session-badge"
                 style={{ fontSize: "9px", padding: "1px 6px",
                          borderRadius: "3px", fontWeight: 700,
                          letterSpacing: "0.05em",
                          color: ms.is_rth ? "#00FF88" : "#FFB800",
                          border: `1px solid ${ms.is_rth ? "#00FF8880" : "#FFB80080"}` }}
                 title={ms.et_time || ""}>
            {ms.state || "unknown"}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "3px", fontFamily: "ui-monospace", fontSize: "10px",
                        color: "#e5e7eb" }}>
          <div>worker_uuid: <span style={{ color: "#8A8A93" }}>{rt.worker_uuid}</span></div>
          <div>pid: <span style={{ color: "#8A8A93" }}>{rt.process_id}</span></div>
          <div>worker_started: <span style={{ color: "#8A8A93" }}>{fmtTs(rt.worker_started_at)}</span></div>
          <div>next_run: <span style={{ color: "#8A8A93" }}>{fmtTs(rt.next_scheduled_run_at)}</span></div>
          <div>last_tick: <span style={{ color: "#8A8A93" }}>{fmtTs(rt.last_tick_at)}</span></div>
          <div>last_attempt: <span style={{ color: "#8A8A93" }}>{fmtTs(rt.last_attempt_at)}</span></div>
          <div>last_success: <span style={{ color: rt.last_success_at ? "#00FF88" : "#8A8A93" }}>{fmtTs(rt.last_success_at)}</span></div>
          <div>last_persist: <span style={{ color: rt.last_persist_at ? "#00FF88" : "#8A8A93" }}>{fmtTs(rt.last_persist_at)}</span></div>
          <div>last_heartbeat: <span style={{ color: "#8A8A93" }}>{fmtTs(rt.last_heartbeat_at)}</span></div>
          <div>ticks (total/exec/skip): <span style={{ color: "#8A8A93" }}>
            {rt.ticks_total ?? 0} / {rt.ticks_executed ?? 0} / {rt.ticks_skipped ?? 0}
          </span></div>
          <div>skip_reason: <span style={{ color: "#FFB800" }}>{rt.skip_reason || "—"}</span></div>
          <div>coll: <span style={{ color: "#8A8A93" }}>{rt.mongo_collection_name}</span></div>
        </div>
        {rt.last_error && (
          <div data-testid="collector-last-error"
                style={{ color: "#fca5a5", fontSize: "10px",
                         fontFamily: "ui-monospace", marginTop: "4px",
                         padding: "4px 6px", background: "#1a0e0e",
                         borderRadius: "3px" }}>
            last_error @ {rt.last_error_at}: {rt.last_error}
          </div>
        )}
      </div>

      <div style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px" }}>
        <div style={{ color: "#8A8A93", fontSize: "10px",
                        textTransform: "uppercase", letterSpacing: "0.08em",
                        marginBottom: "4px" }}>
          Metrics since process start
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "8px", fontFamily: "ui-monospace", fontSize: "11px" }}>
          <div>attempts <br/><span data-testid="metric-attempts"
                                      style={{ color: "#e5e7eb", fontSize: "13px" }}>
            {m.raw_collection_attempts ?? 0}
          </span></div>
          <div>persisted <br/><span data-testid="metric-persisted"
                                       style={{ color: "#00FF88", fontSize: "13px" }}>
            {m.records_persisted ?? 0}
          </span></div>
          <div>duplicates <br/><span data-testid="metric-duplicates"
                                        style={{ color: "#FFB800", fontSize: "13px" }}>
            {m.duplicates_suppressed ?? 0}
          </span></div>
          <div>errors <br/><span data-testid="metric-errors"
                                    style={{ color: "#fca5a5", fontSize: "13px" }}>
            {m.collector_errors ?? 0}
          </span></div>
        </div>
      </div>

      {tickResult && (
        <div data-testid="manual-tick-result"
              style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px",
                       fontFamily: "ui-monospace", fontSize: "10px",
                       color: "#e5e7eb" }}>
          {tickResult.error ? (
            <div style={{ color: "#fca5a5" }}>tick error: {tickResult.error}</div>
          ) : (
            <>
              <div><b>manual tick</b> {tickResult.skipped
                ? <span style={{ color: "#FFB800" }}>skipped ({tickResult.skip_reason})</span>
                : <span style={{ color: "#00FF88" }}>
                    executed · {tickResult.n_persisted ?? 0} persisted · {tickResult.n_errors ?? 0} errors
                  </span>}
              </div>
              {tickResult.attempts && tickResult.attempts.length > 0 && (
                <div style={{ marginTop: "3px", color: "#8A8A93" }}>
                  {tickResult.attempts.map((a, i) => (
                    <span key={i} style={{ marginRight: "6px" }}>
                      {a.ticker}:<span style={{
                        color: a.persisted ? "#00FF88"
                                : a.error   ? "#fca5a5"
                                : "#FFB800"
                      }}>
                        {a.persisted ? "persisted" : a.error ? "error" : (a.reason || "dedup")}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Iter122.7.1b — Outcome Enrichment runtime block */}
      <EnrichmentRuntimeBlock health={health} onRefresh={refresh} />
    </div>
  );
}

function EnrichmentRuntimeBlock({ health, onRefresh }) {
  const [ticking, setTicking] = useState(false);
  const [tickResult, setTickResult] = useState(null);
  const er = health?.enrichment_runtime || {};

  const runEnrichmentTick = async () => {
    setTicking(true); setTickResult(null);
    try {
      const r = await axios.post(
        `${API}/forge/gex/v2/collector/enrichment-tick-now`,
        { batch_limit: 20 });
      setTickResult(r.data);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setTickResult({ error: e?.response?.data?.detail || String(e) });
    } finally {
      setTicking(false);
    }
  };

  return (
    <div data-testid="enrichment-runtime-block"
          style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px",
                      marginBottom: "4px", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ color: "#8A8A93", fontSize: "10px",
                            textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Outcome Enrichment runtime
          </span>
          <span data-testid="enrichment-scheduler-badge"
                 style={{ fontSize: "9px", padding: "1px 6px",
                          borderRadius: "3px", fontWeight: 700,
                          letterSpacing: "0.05em",
                          color: er.scheduler_running ? "#00FF88" : "#fca5a5",
                          border: `1px solid ${er.scheduler_running ? "#00FF8880" : "#fca5a580"}` }}>
            {er.scheduler_running ? "RUNNING" : "STOPPED"}
          </span>
        </div>
        <button data-testid="enrichment-tick-now-btn"
                 onClick={runEnrichmentTick} disabled={ticking}
                 title="Run one outcome-enrichment batch (writes to gex_v2_outcome_enrichment)"
                 style={{ background: "transparent", color: "#00FF88",
                          border: "1px solid #00FF8880", borderRadius: "3px",
                          padding: "3px 8px", fontSize: "10px",
                          fontWeight: 700, cursor: ticking ? "wait" : "pointer",
                          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {ticking ? "Enriching…" : "Enrich Now"}
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "3px", fontFamily: "ui-monospace", fontSize: "10px",
                      color: "#e5e7eb" }}>
        <div>job_id: <span style={{ color: "#8A8A93" }}>{er.job_id}</span></div>
        <div>worker_uuid: <span style={{ color: "#8A8A93" }}>{er.worker_uuid}</span></div>
        <div>next_run: <span style={{ color: "#8A8A93" }}>{fmtTs(er.next_scheduled_run_at)}</span></div>
        <div>last_tick: <span style={{ color: "#8A8A93" }}>{fmtTs(er.last_tick_at)}</span></div>
        <div>last_success: <span style={{ color: er.last_success_at ? "#00FF88" : "#8A8A93" }}>{fmtTs(er.last_success_at)}</span></div>
        <div>ticks (total/w-work): <span style={{ color: "#8A8A93" }}>
          {er.ticks_total ?? 0} / {er.ticks_with_work ?? 0}
        </span></div>
        <div>enrichments (created/updated): <span style={{ color: "#8A8A93" }}>
          {er.enrichments_created ?? 0} / {er.enrichments_updated ?? 0}
        </span></div>
        <div>completed: <span data-testid="enrichment-completed"
                                  style={{ color: "#00FF88" }}>
          {er.enrichments_completed ?? 0}
        </span></div>
        <div>horizons_computed: <span style={{ color: "#e5e7eb" }}>{er.horizons_computed ?? 0}</span></div>
        <div>last batch: <span style={{ color: "#8A8A93" }}>
          {er.last_batch_size ?? 0} examined · {er.last_batch_created ?? 0} new · {er.last_batch_updated ?? 0} upd
        </span></div>
      </div>
      {er.last_error && (
        <div data-testid="enrichment-last-error"
              style={{ color: "#fca5a5", fontSize: "10px",
                       fontFamily: "ui-monospace", marginTop: "4px",
                       padding: "4px 6px", background: "#1a0e0e",
                       borderRadius: "3px" }}>
          last_error @ {er.last_error_at}: {er.last_error}
        </div>
      )}
      {tickResult && (
        <div data-testid="enrichment-tick-result"
              style={{ marginTop: "6px", fontFamily: "ui-monospace",
                       fontSize: "10px", color: "#e5e7eb" }}>
          {tickResult.error ? (
            <div style={{ color: "#fca5a5" }}>enrichment error: {tickResult.error}</div>
          ) : tickResult.skipped ? (
            <div style={{ color: "#FFB800" }}>
              skipped ({tickResult.skip_reason})
            </div>
          ) : (
            <div style={{ color: "#00FF88" }}>
              examined {tickResult.n_examined ?? 0} · created {tickResult.n_created ?? 0}
              {" · "}updated {tickResult.n_updated ?? 0} · horizons {tickResult.horizons_computed ?? 0}
              {" · "}completed {tickResult.n_completed ?? 0}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
  } catch { return iso; }
}


function ValidationWindowPanel({ isAdmin }) {
  const [state, setState] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acks, setAcks]   = useState({
    collector_deployment_verified: false,
    wes_live_validation_verified:  false,
    schema_version_confirmed:      false,
    outcome_definition_locked:     false,
  });

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/forge/gex/v2/validation-window`);
      setState(r.data);
    } catch { setState(null); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const start = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/forge/gex/v2/validation-window/start`, {
        trading_days: 30,
        notes: "Manually activated via /shadow diagnostics workspace",
        acknowledgements: acks,
      });
      await refresh();
      setShowConfirm(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to start");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    if (!window.confirm("Stop the active validation window? This is preserved for audit."))
      return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/forge/gex/v2/validation-window/stop`, {
        reason: "Manually stopped via /shadow diagnostics workspace",
      });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to stop");
    } finally { setBusy(false); }
  };

  const allAcksTrue = Object.values(acks).every(Boolean);

  return (
    <div data-testid="validation-window-panel"
          style={{ border: "1px solid #26262B", background: "#121216",
                   borderRadius: "4px", padding: "14px 16px",
                   display: "flex", flexDirection: "column", gap: "10px" }}>
      <h3 style={{ margin: 0, color: "#fff", fontSize: "12px",
                     textTransform: "uppercase", letterSpacing: "0.1em" }}>
        30-Trading-Day Validation Window
      </h3>
      <div style={{ fontSize: "11px", color: "#8A8A93" }}>
        Start the formal validation window ONLY after collector health is
        verified, WES live-validation is confirmed, schema/version is locked,
        and outcome definitions are frozen.
      </div>
      {!state?.active && (
        <div data-testid="validation-window-inactive"
              style={{ fontSize: "12px", color: "#8A8A93" }}>
          Status: <b style={{ color: "#8A8A93" }}>inactive</b> — v2
          remains in shadow. No certification clock is running.
        </div>
      )}
      {state?.active && (
        <div data-testid="validation-window-active"
              style={{ fontSize: "12px", color: "#e5e7eb",
                       display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                       gap: "6px" }}>
          <div>Status: <span style={{ color: "#00FF88" }}>active</span></div>
          <div>Trading days: <span style={{ color: "#e5e7eb" }}>{state.validation_trading_days}</span></div>
          <div>Started at:
            <span style={{ color: "#e5e7eb", fontFamily: "ui-monospace" }}>
              {" "}{state.validation_start_at}
            </span>
          </div>
          <div>Target end:
            <span style={{ color: "#e5e7eb", fontFamily: "ui-monospace" }}>
              {" "}{state.validation_target_end_at}
            </span>
          </div>
          <div>Method: <span style={{ color: "#e5e7eb", fontFamily: "ui-monospace" }}>
            {state.collection_method_version}
          </span></div>
          <div>Outcome defs: <span style={{ color: "#e5e7eb", fontFamily: "ui-monospace" }}>
            {state.outcome_definition_version}
          </span></div>
        </div>
      )}
      {isAdmin && (
        <div style={{ borderTop: "1px solid #1A1A20", paddingTop: "10px" }}>
          {!state?.active && !showConfirm && (
            <button data-testid="validation-window-start-btn"
                     onClick={() => setShowConfirm(true)}
                     style={{ background: "#00FF88", color: "#08080A",
                              border: 0, borderRadius: "4px",
                              padding: "8px 14px", fontSize: "12px",
                              fontWeight: 700, cursor: "pointer" }}>
              Start 30-Day Validation Window
            </button>
          )}
          {!state?.active && showConfirm && (
            <div style={{ display: "flex", flexDirection: "column",
                            gap: "6px", fontSize: "11px", color: "#e5e7eb" }}>
              <div style={{ color: "#FFB800", marginBottom: "4px" }}>
                Confirm each acknowledgement to enable Start:
              </div>
              {Object.keys(acks).map((k) => (
                <label key={k} style={{ display: "flex", gap: "6px",
                                          alignItems: "center", cursor: "pointer" }}
                        data-testid={`ack-${k}`}>
                  <input type="checkbox" checked={acks[k]}
                          onChange={(e) => setAcks({ ...acks, [k]: e.target.checked })} />
                  {k.replace(/_/g, " ")}
                </label>
              ))}
              <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                <button
                  data-testid="validation-window-confirm-start"
                  onClick={start}
                  disabled={!allAcksTrue || busy}
                  style={{
                    background:  allAcksTrue ? "#00FF88" : "#334155",
                    color:       allAcksTrue ? "#08080A" : "#8A8A93",
                    border: 0, borderRadius: "4px",
                    padding: "6px 12px", fontSize: "11px",
                    fontWeight: 700, cursor: (allAcksTrue && !busy) ? "pointer" : "not-allowed",
                  }}>
                  {busy ? "Starting…" : "Confirm Start"}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  style={{ background: "transparent", color: "#8A8A93",
                              border: "1px solid #26262B", borderRadius: "4px",
                              padding: "6px 12px", fontSize: "11px",
                              cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {state?.active && (
            <button data-testid="validation-window-stop-btn"
                     onClick={stop}
                     disabled={busy}
                     style={{ background: "#7f1d1d", color: "#fff",
                              border: 0, borderRadius: "4px",
                              padding: "8px 14px", fontSize: "12px",
                              fontWeight: 700, cursor: busy ? "wait" : "pointer" }}>
              {busy ? "Stopping…" : "Stop Validation Window"}
            </button>
          )}
          {err && <div data-testid="validation-window-error"
                          style={{ color: "#fca5a5", fontSize: "11px", marginTop: "6px" }}>
            {err}
          </div>}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter122.7.1 — Diagnostic Sweep panel (admin only)
// Non-executing preflight tool.  Uses:
//   POST /api/forge/gex/v2/collector/session-sweep
//   GET  /api/forge/gex/v2/collector/session-sweep/{sweep_id}
// The panel emphasises the "NOT FULL-SESSION CERTIFICATION" label
// throughout so operators do not misuse the report as evidence for
// starting the 30-trading-day validation window.
// ---------------------------------------------------------------------------
// Iter122.7.1 §2 — Terminal per-ticker status → chip style.
const TERMINAL_STATUS_STYLE = {
  PASS_OPERATIONAL:      { bg: "#12351d", fg: "#c7f7d0", label: "OPERATIONAL" },
  PASS_INFORMATIONAL:    { bg: "#1f2937", fg: "#e5e7eb", label: "INFORMATIONAL" },
  PASS_EXPECTED_NO_DATA: { bg: "#22222f", fg: "#e2e2f0", label: "NO DATA" },
  WARN_DEGRADED:         { bg: "#78500a", fg: "#ffd18a", label: "DEGRADED" },
  FAIL_SYSTEM:           { bg: "#7f1d1d", fg: "#fecaca", label: "FAIL" },
};

function ReadinessGatePanel({ isAdmin }) {
  const [gate, setGate]     = useState(null);
  const [report, setReport] = useState(null);
  const [audit, setAudit]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState(null);

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const [g, r, a] = await Promise.all([
        axios.get(`${API}/forge/gex/v2/collector/readiness-gate`),
        axios.get(`${API}/forge/gex/v2/collector/enrichment-report`),
        axios.get(`${API}/forge/gex/v2/collector/immutability-audit?limit=3`),
      ]);
      setGate(g.data); setReport(r.data); setAudit(a.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  const statusColor = (s) => (
    s === "pass" ? "#00FF88" :
    s === "pending" ? "#FFB800" :
    s === "already_started" ? "#8b5cf6" :
    s === "warn" ? "#f59e0b" :
    "#fca5a5"
  );

  return (
    <div data-testid="readiness-gate-panel"
          style={{ border: "1px solid #26262B", background: "#121216",
                   borderRadius: "4px", padding: "14px 16px",
                   display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", flexWrap: "wrap",
                      gap: "8px" }}>
        <h3 style={{ margin: 0, color: "#fff", fontSize: "12px",
                       textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Production Readiness Gate
        </h3>
        <button data-testid="readiness-gate-run-btn"
                 onClick={run} disabled={loading}
                 style={{ background: "transparent", color: "#00FF88",
                          border: "1px solid #00FF8880", borderRadius: "3px",
                          padding: "5px 12px", fontSize: "11px",
                          fontWeight: 700, cursor: loading ? "wait" : "pointer",
                          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {loading ? "Checking…" : (gate ? "Re-check" : "Run gate")}
        </button>
      </div>

      <div style={{ color: "#8A8A93", fontSize: "11px", lineHeight: "1.4" }}>
        Composite verdict on the eight readiness gates. This report is <b>read-only</b> —
        it does NOT start the validation window. Step-8 acknowledgement remains required.
      </div>

      {err && (
        <div data-testid="readiness-gate-error"
              style={{ color: "#fca5a5", fontSize: "11px" }}>
          error: {err}
        </div>
      )}

      {gate && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span data-testid="readiness-gate-verdict"
                   style={{ padding: "4px 10px", borderRadius: "3px",
                            fontSize: "11px", fontWeight: 700,
                            letterSpacing: "0.08em", textTransform: "uppercase",
                            color: "#08080A",
                            background: gate.all_green ? "#00FF88" : "#FFB800" }}>
              {gate.all_green ? "READY" : "NOT READY"}
            </span>
            <span style={{ color: "#e5e7eb", fontSize: "11px",
                             fontFamily: "ui-monospace" }}>
              {(gate.gates || []).filter(g => g.status === "pass").length}
              /{(gate.gates || []).length} gates pass
            </span>
          </div>
          <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
                          gap: "6px", fontFamily: "ui-monospace",
                          fontSize: "10px" }}>
            {(gate.gates || []).map((g, i) => (
              <div key={i}
                    data-testid={`readiness-gate-${g.gate}`}
                    style={{ padding: "6px 8px", background: "#08080A",
                             border: "1px solid #1A1A20", borderRadius: "3px" }}>
                <div style={{ color: "#e5e7eb" }}>{g.gate}</div>
                <div style={{ color: statusColor(g.status), fontWeight: 700,
                                textTransform: "uppercase" }}>
                  {g.status}
                </div>
              </div>
            ))}
          </div>
          <div data-testid="readiness-gate-recommendation"
                style={{ padding: "8px 10px", background: "#08080A",
                         border: "1px solid #1A1A20", borderRadius: "3px",
                         color: "#e5e7eb", fontSize: "11px",
                         fontStyle: "italic", lineHeight: "1.5" }}>
            {gate.recommendation}
          </div>
        </>
      )}

      {report && (
        <details data-testid="enrichment-report-details"
                  style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px" }}>
          <summary style={{ color: "#8A8A93", fontSize: "11px",
                              cursor: "pointer", letterSpacing: "0.05em" }}>
            Enrichment progress ({report.total_enrichment_records ?? 0} records)
          </summary>
          <div style={{ marginTop: "6px", display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)", gap: "4px",
                          fontFamily: "ui-monospace", fontSize: "10px",
                          color: "#e5e7eb" }}>
            <div>backlog: <b>{report.eligible_records_pending}</b></div>
            <div>1h done: <span style={{ color: "#00FF88" }}>{report.outcomes_1h_completed}</span></div>
            <div>1h pending: {report.outcomes_1h_pending}</div>
            <div>1D done: <span style={{ color: "#00FF88" }}>{report.outcomes_1_session_completed}</span></div>
            <div>1D pending: {report.outcomes_1_session_pending}</div>
            <div>1D no_data: {report.outcomes_1_session_no_data}</div>
            <div>3D done: <span style={{ color: "#00FF88" }}>{report.outcomes_3_sessions_completed}</span></div>
            <div>3D pending: {report.outcomes_3_sessions_pending}</div>
            <div>3D no_data: {report.outcomes_3_sessions_no_data}</div>
          </div>
          <div style={{ marginTop: "6px", fontSize: "10px", color: "#8A8A93",
                          fontFamily: "ui-monospace" }}>
            eligibility: {JSON.stringify(report.validation_eligibility_tally || {})}
          </div>
        </details>
      )}

      {audit && (
        <details data-testid="immutability-audit-details"
                  style={{ borderTop: "1px solid #1A1A20", paddingTop: "8px" }}>
          <summary style={{ color: "#8A8A93", fontSize: "11px",
                              cursor: "pointer", letterSpacing: "0.05em" }}>
            Immutability audit ({audit.sample_count} samples · no-look-ahead check)
          </summary>
          {audit.sample_count === 0 ? (
            <div style={{ marginTop: "6px", padding: "6px 8px",
                            background: "#08080A", border: "1px solid #1A1A20",
                            borderRadius: "3px", color: "#8A8A93",
                            fontFamily: "ui-monospace", fontSize: "10px",
                            fontStyle: "italic" }}>
              No completed 1h enrichments yet — audit will populate after
              the first backfill cycle completes.
            </div>
          ) : (
            <div style={{ marginTop: "6px", fontFamily: "ui-monospace",
                            fontSize: "10px", color: "#e5e7eb" }}>
              {(audit.samples || []).map((s, i) => (
                <div key={i} style={{ padding: "6px 8px", marginBottom: "4px",
                                         background: "#08080A",
                                         border: "1px solid #1A1A20", borderRadius: "3px" }}>
                  <div><b>{s.signal_ticker}</b> · {s.signal_captured_at_utc}</div>
                  <div>regime: {s.signal_regime} ({s.signal_regime_reason_code})</div>
                  <div>look-ahead check: <span style={{
                    color: s.look_ahead_check_passed ? "#00FF88" : "#fca5a5"
                  }}>{s.look_ahead_check_passed ? "PASSED" : "FAILED"}</span></div>
                  <div>eligibility: {s.enrichment_validation_eligibility}</div>
                </div>
              ))}
            </div>
          )}
        </details>
      )}
    </div>
  );
}


function DiagnosticSweepPanel({ isAdmin }) {
  const [config,    setConfig]    = useState(null);
  const [confirm,   setConfirm]   = useState(false);
  const [starting,  setStarting]  = useState(false);
  const [sweepId,   setSweepId]   = useState(null);
  const [sweep,     setSweep]     = useState(null);
  const [acceptance, setAcceptance] = useState(null);
  const [err,       setErr]       = useState(null);
  const [override,  setOverride]  = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    axios.get(`${API}/forge/gex/v2/collector/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  // Poll sweep status while running.
  useEffect(() => {
    if (!sweepId) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/forge/gex/v2/collector/session-sweep/${sweepId}`);
        if (!alive) return;
        setSweep(r.data);
        const s = r.data?.status;
        if (s === "completed" || s === "partially_completed" || s === "failed") {
          // Fetch acceptance verdict once the sweep is terminal.
          try {
            const ar = await axios.get(
              `${API}/forge/gex/v2/collector/session-sweep/${sweepId}/acceptance`);
            if (alive) setAcceptance(ar.data);
          } catch { /* non-fatal */ }
          return;   // stop polling
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || "Failed to fetch sweep status");
        return;
      }
      setTimeout(tick, 2500);
    };
    tick();
    return () => { alive = false; };
  }, [sweepId]);

  const start = async () => {
    setStarting(true); setErr(null); setAcceptance(null);
    try {
      const payload = {
        tickers:              RECOMMENDED_TICKERS,
        override_concurrency: override,
        mode:                 "diagnostic",
        persist_to_forward_validation: false,
      };
      if (override) payload.override_reason = overrideReason.trim();
      const r = await axios.post(
        `${API}/forge/gex/v2/collector/session-sweep`, payload,
      );
      setSweepId(r.data.sweep_id);
      setSweep({ ...r.data, per_ticker: Object.fromEntries(
        RECOMMENDED_TICKERS.map((t) => [t, { status: "pending" }])
      )});
      setConfirm(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to start sweep");
    } finally {
      setStarting(false);
    }
  };

  const exportReport = () => {
    if (!sweep) return;
    const blob = new Blob([JSON.stringify(sweep, null, 2)],
                            { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sweep-${sweep.sweep_id || sweepId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) return null;

  const env = config?.environment || "unknown";
  const envColour = env === "production" ? "#FF3366"
                    : (env === "preview" ? "#FFB800" : "#8A8A93");

  const per = sweep?.per_ticker || {};
  const aggr = sweep?.aggregates || {};

  return (
    <div data-testid="diagnostic-sweep-panel"
          style={{ border: "1px solid #26262B", background: "#121216",
                   borderRadius: "4px", padding: "14px 16px",
                   display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: "10px", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, color: "#fff", fontSize: "12px",
                       textTransform: "uppercase", letterSpacing: "0.1em" }}
             data-testid="diagnostic-sweep-title">
          Run Diagnostic Sweep
        </h3>
        <span data-testid="sweep-not-cert-label"
                style={{ fontSize: "10px", fontWeight: 700,
                         letterSpacing: "0.06em", textTransform: "uppercase",
                         color: "#FFB800",
                         border: "1px solid #FFB80080", padding: "2px 8px",
                         borderRadius: "3px" }}>
          DIAGNOSTIC SWEEP — NOT FULL-SESSION CERTIFICATION
        </span>
        <span data-testid="sweep-environment"
                style={{ fontSize: "10px", fontWeight: 700,
                         letterSpacing: "0.06em", textTransform: "uppercase",
                         color: envColour,
                         border: `1px solid ${envColour}80`, padding: "2px 8px",
                         borderRadius: "3px" }}>
          env: {env}
        </span>
      </div>
      <div style={{ fontSize: "11px", color: "#8A8A93", lineHeight: 1.5 }}>
        Iterates the 11-ticker recommended universe and calls the v2
        engine once per symbol. Records latency, WES source, and
        fallbacks. Results are diagnostic only — they DO NOT count
        toward the 30-trading-day validation sample and are NOT valid
        evidence for starting the certification clock.
      </div>

      {config && (
        <div style={{ fontSize: "11px", color: "#e5e7eb",
                        display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {RECOMMENDED_TICKERS.map((t) => {
            const missing = (config.missing_recommended || []).includes(t);
            const row = per[t] || {};
            const term = row.terminal_status;
            const style = term && TERMINAL_STATUS_STYLE[term];
            let bg, fg, label;
            if (style) { bg = style.bg; fg = style.fg; label = style.label; }
            else if (row.status === "running") { bg = "#78500a"; fg = "#ffd18a"; label = "RUN"; }
            else                               { bg = "#22222f"; fg = "#e2e2f0"; label = "IDLE"; }
            const title = term
              ? `${t} — ${term}${row.terminal_rationale ? " — " + row.terminal_rationale : ""}`
              : (row.v2_error ? `${t} — ${row.v2_error}`
                  : (missing ? `${t} not in deployed GEX_COLLECTOR_TICKERS` : t));
            return (
              <span key={t}
                     data-testid={`sweep-ticker-${t}`}
                     data-status={row.status || "idle"}
                     data-terminal={term || ""}
                     style={{ padding: "3px 8px", fontSize: "10px",
                              fontWeight: 700, letterSpacing: "0.04em",
                              backgroundColor: bg, color: fg,
                              borderRadius: "3px", fontFamily: "ui-monospace",
                              opacity: missing ? 0.55 : 1,
                              display: "inline-flex", gap: "6px",
                              alignItems: "center" }}
                     title={title}>
                <b>{t}</b>
                <span style={{ opacity: 0.8, fontSize: "9px" }}>{label}</span>
              </span>
            );
          })}
        </div>
      )}

      {config?.missing_recommended?.length > 0 && (
        <div data-testid="sweep-missing-recommended"
              style={{ background: "#3f2f0e", color: "#ffd18a",
                       padding: "6px 10px", borderRadius: "3px",
                       fontSize: "11px" }}>
          Missing from deployed collector config:{" "}
          <b>{config.missing_recommended.join(", ")}</b>. The sweep will
          still run these tickers — update GEX_COLLECTOR_TICKERS before
          starting the certification clock.
        </div>
      )}

      {err && (
        <div data-testid="sweep-error"
              style={{ background: "#3b1616", color: "#fca5a5",
                       padding: "6px 10px", borderRadius: "3px",
                       fontSize: "11px" }}>{err}</div>
      )}

      {!confirm && !sweep && (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button data-testid="diagnostic-sweep-open-confirm"
                   onClick={() => setConfirm(true)}
                   style={{ background: "#FFB800", color: "#08080A",
                            border: 0, borderRadius: "4px",
                            padding: "8px 14px", fontSize: "12px",
                            fontWeight: 700, cursor: "pointer" }}>
            Run Diagnostic Sweep
          </button>
        </div>
      )}
      {confirm && !sweep && (
        <div style={{ background: "#0A0A0D", border: "1px solid #26262B",
                        borderRadius: "3px", padding: "10px 12px",
                        display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ color: "#FFB800", fontSize: "11px", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Confirm sweep execution — environment: {env}
          </div>
          <div style={{ color: "#8A8A93", fontSize: "11px" }}>
            This will invoke the v2 engine once per ticker (11 requests).
            No orders are placed, no forward-validation samples are
            persisted, no production defaults change.
          </div>
          <label style={{ fontSize: "11px", color: "#e5e7eb",
                             display: "flex", gap: "6px", alignItems: "center" }}
                  data-testid="sweep-override-label">
            <input type="checkbox" checked={override}
                    onChange={(e) => setOverride(e.target.checked)}
                    data-testid="sweep-override-checkbox" />
            Bypass cooldown / concurrency guard (admin discretion)
          </label>
          {override && (
            <div data-testid="sweep-override-reason-wrapper"
                  style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "10px", color: "#FFB800",
                                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Override reason (required)
              </label>
              <input
                data-testid="sweep-override-reason"
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="e.g. Rerunning after config change"
                style={{ background: "#0A0A0D", border: "1px solid #26262B",
                            color: "#fff", padding: "6px 8px",
                            fontSize: "12px", borderRadius: "3px",
                            fontFamily: "ui-sans-serif, system-ui" }} />
            </div>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            <button data-testid="diagnostic-sweep-start"
                     onClick={start}
                     disabled={starting || (override && !overrideReason.trim())}
                     style={{ background: (override && !overrideReason.trim()) ? "#334155" : "#FFB800",
                              color: (override && !overrideReason.trim()) ? "#8A8A93" : "#08080A",
                              border: 0, borderRadius: "4px",
                              padding: "7px 12px", fontSize: "11px",
                              fontWeight: 700,
                              cursor: (starting || (override && !overrideReason.trim())) ? "not-allowed" : "pointer" }}>
              {starting ? "Starting…" : "Confirm Start"}
            </button>
            <button data-testid="diagnostic-sweep-cancel"
                     onClick={() => setConfirm(false)}
                     style={{ background: "transparent", color: "#8A8A93",
                              border: "1px solid #26262B", borderRadius: "4px",
                              padding: "7px 12px", fontSize: "11px",
                              cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {sweep && (
        <div data-testid="sweep-report"
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap",
                          alignItems: "center", fontSize: "11px",
                          color: "#e5e7eb", fontFamily: "ui-monospace" }}>
            <span data-testid="sweep-status">
              status: <b style={{ color: sweep.status === "completed" ? "#00FF88"
                                    : sweep.status === "failed" ? "#fca5a5"
                                    : "#FFB800" }}>{sweep.status}</b>
            </span>
            <span>sweep_id: {sweep.sweep_id || sweepId}</span>
            {aggr.by_terminal_status && (
              <span data-testid="sweep-by-terminal">
                pass_op: <b style={{ color: "#00FF88" }}>{aggr.by_terminal_status.PASS_OPERATIONAL ?? 0}</b>
                {" · "}pass_info: {aggr.by_terminal_status.PASS_INFORMATIONAL ?? 0}
                {" · "}no_data: {aggr.by_terminal_status.PASS_EXPECTED_NO_DATA ?? 0}
                {" · "}degraded: <b style={{ color: "#FFB800" }}>{aggr.by_terminal_status.WARN_DEGRADED ?? 0}</b>
                {" · "}fail: <b style={{ color: "#fca5a5" }}>{aggr.by_terminal_status.FAIL_SYSTEM ?? 0}</b>
              </span>
            )}
            <span>avg lat: {aggr.avg_latency_ms ?? "—"} ms</span>
            <span>p95 lat: {aggr.p95_latency_ms ?? "—"} ms</span>
            <span>wes live/stale/na: {aggr.wes_live ?? 0}/{aggr.wes_stale ?? 0}/{aggr.wes_unavailable ?? 0}</span>
            {sweep.override_audit && (
              <span data-testid="sweep-override-audit"
                     title={JSON.stringify(sweep.override_audit)}
                     style={{ color: "#FFB800" }}>
                ⚠ override: {sweep.override_audit.reason}
              </span>
            )}
          </div>

          {acceptance && (
            <div data-testid="sweep-acceptance"
                  style={{ background: "#0A0A0D", border: `1px solid ${acceptance.accepted ? "#00FF8880" : "#fca5a580"}`,
                            borderRadius: "3px", padding: "8px 10px",
                            display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "center", fontSize: "11px" }}>
                <div>
                  <b style={{ color: acceptance.accepted ? "#00FF88" : "#fca5a5" }}
                      data-testid="acceptance-verdict">
                    Preflight acceptance:{" "}
                    {acceptance.accepted ? "PASS" : "BLOCKED"}
                  </b>
                  <span style={{ marginLeft: "8px", color: "#8A8A93" }}>
                    {acceptance.summary}
                  </span>
                </div>
                <span style={{ fontSize: "9px", padding: "2px 6px",
                                  borderRadius: "3px", fontWeight: 700,
                                  letterSpacing: "0.06em", textTransform: "uppercase",
                                  border: `1px solid ${acceptance.production_readiness_evidence ? "#00FF8880" : "#FFB80080"}`,
                                  color: acceptance.production_readiness_evidence ? "#00FF88" : "#FFB800" }}
                        data-testid="acceptance-prod-evidence">
                  prod evidence: {String(!!acceptance.production_readiness_evidence)}
                </span>
              </div>
              <div style={{ display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: "4px", fontSize: "10px",
                              fontFamily: "ui-monospace" }}>
                {(acceptance.gates || []).map((g) => (
                  <div key={g.name}
                        data-testid={`gate-${g.name}`}
                        style={{ padding: "3px 6px", borderRadius: "3px",
                                    background: g.status === "pass" ? "#12351d" : "#3b1616",
                                    color:      g.status === "pass" ? "#c7f7d0" : "#fca5a5" }}
                        title={g.detail}>
                    {g.status === "pass" ? "✓" : "✗"} {g.name}
                  </div>
                ))}
              </div>
              <div style={{ color: "#8A8A93", fontSize: "10px",
                              fontStyle: "italic" }}>
                {acceptance.note}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "6px" }}>
            <button data-testid="sweep-report-export"
                     onClick={exportReport}
                     style={{ background: "transparent", color: "#00FF88",
                              border: "1px solid #00FF8880", borderRadius: "3px",
                              padding: "5px 10px", fontSize: "11px",
                              cursor: "pointer" }}>
              Export JSON
            </button>
            <button data-testid="sweep-report-clear"
                     onClick={() => { setSweep(null); setSweepId(null); setAcceptance(null); setOverrideReason(""); }}
                     style={{ background: "transparent", color: "#8A8A93",
                              border: "1px solid #26262B", borderRadius: "3px",
                              padding: "5px 10px", fontSize: "11px",
                              cursor: "pointer" }}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ShadowMode() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { status, loading } = useShadowStatus();
  const [symbol, setSymbol]   = useState("SPY");
  const [draft, setDraft]     = useState("SPY");
  const [diag, setDiag]       = useState(null);
  const [diagErr, setDiagErr] = useState(null);
  const [busy, setBusy]       = useState(false);

  const eligible = status?.shadow_eligible === true;

  const fetchDiag = useCallback(async (sym) => {
    setBusy(true); setDiagErr(null); setDiag(null);
    try {
      const r = await axios.get(`${API}/forge/gex/v2/diagnostics`, {
        params: { symbol: sym },
      });
      setDiag(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load diagnostics";
      setDiagErr(msg);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (eligible) fetchDiag(symbol);
  }, [eligible, symbol, fetchDiag]);

  const v1 = diag?.v1?.payload;
  const v2 = diag?.v2?.payload;

  const v2Rendered = useMemo(() => {
    if (!v2) return [];
    // v2 emits arrays across several blocks.  Concatenate them all here.
    const buckets = [
      ...(v2?.market_state?.rendered || []),
      ...(v2?.path_status?.rendered  || []),
      ...(v2?.walls?.rendered        || []),
      ...(v2?.zero_gamma_info?.rendered || []),
      ...(v2?.confidence?.rendered   || []),
      ...(v2?.rendered               || []),
    ];
    return buckets;
  }, [v2]);

  if (loading) return (
    <div style={{ padding: "24px" }}>
      <p style={{ color: "#8A8A93", fontFamily: "ui-monospace" }}>Loading…</p>
    </div>
  );

  if (!eligible) {
    return (
      <div data-testid="shadow-mode-not-eligible"
            style={{ padding: "24px", maxWidth: "620px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 900, color: "#fff",
                       margin: "0 0 12px 0" }}>
          Shadow Mode Workspace
        </h1>
        <div style={{ border: "1px solid #26262B", background: "#121216",
                        borderRadius: "4px", padding: "14px 16px",
                        color: "#8A8A93", fontSize: "13px", lineHeight: 1.6 }}>
          This diagnostics workspace is restricted to internal testers and
          administrators. v1 remains the production default across the app.
          Please contact an administrator to request access.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shadow-mode-page"
          className="space-y-4"
          style={{ padding: "0 4px", display: "flex", flexDirection: "column",
                   gap: "16px" }}>
      <div>
        <h1 style={{ fontSize: "22px", fontWeight: 900, color: "#fff",
                       margin: "0 0 4px 0" }}>
          Shadow Mode — v1 vs v2 Diagnostics
        </h1>
        <div style={{ color: "#8A8A93", fontSize: "12px" }}>
          Validation-only workspace for internal testers. v1 remains the
          production default — nothing on this page executes trades or
          overrides Wheel decisions.
        </div>
      </div>

      <ShadowBanner compact />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                     gap: "12px" }}>
        <CollectorHealthPanel />
        <ValidationWindowPanel isAdmin={isAdmin} />
      </div>

      <DiagnosticSweepPanel isAdmin={isAdmin} />
      <ReadinessGatePanel isAdmin={isAdmin} />

      <div style={{ border: "1px solid #26262B", background: "#121216",
                     borderRadius: "4px", padding: "14px 16px",
                     display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px",
                        flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, color: "#fff", fontSize: "12px",
                         textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Compare a Symbol
          </h3>
          <form
            onSubmit={(e) => { e.preventDefault();
                                 setSymbol((draft || "SPY").toUpperCase()); }}
            style={{ display: "flex", gap: "6px" }}
          >
            <input
              data-testid="shadow-symbol-input"
              type="text" value={draft}
              onChange={(e) => setDraft(e.target.value.toUpperCase())}
              placeholder="Symbol"
              style={{ background: "#08080A", border: "1px solid #26262B",
                          color: "#fff", padding: "6px 10px",
                          fontFamily: "ui-monospace", fontSize: "12px",
                          borderRadius: "3px", width: "100px" }}
            />
            <button data-testid="shadow-symbol-go"
                     type="submit"
                     style={{ background: "#00FF88", color: "#08080A",
                              border: 0, borderRadius: "3px",
                              padding: "6px 12px", fontSize: "12px",
                              fontWeight: 700, cursor: "pointer" }}>
              Compare
            </button>
          </form>
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {CANONICAL_TICKERS.map((t) => (
              <button key={t}
                       data-testid={`shadow-quick-${t}`}
                       onClick={() => { setDraft(t); setSymbol(t); }}
                       style={{ background: symbol === t ? "#26262B" : "transparent",
                                border: "1px solid #26262B", color: "#8A8A93",
                                padding: "4px 8px", fontSize: "11px",
                                borderRadius: "3px", cursor: "pointer",
                                fontFamily: "ui-monospace" }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {busy && (
          <div data-testid="shadow-diag-loading"
                style={{ color: "#8A8A93", fontSize: "12px" }}>
            Loading diagnostics for {symbol}…
          </div>
        )}
        {diagErr && (
          <div data-testid="shadow-diag-error"
                style={{ color: "#fca5a5", fontSize: "12px" }}>
            {diagErr}
          </div>
        )}

        {diag && !busy && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                            gap: "0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse",
                                gridColumn: "1 / span 3",
                                background: "#0A0A0D", border: "1px solid #26262B" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 10px",
                                    color: "#8A8A93", fontSize: "10px",
                                    textTransform: "uppercase", width: "160px" }}>Field</th>
                    <th style={{ textAlign: "left", padding: "8px 10px",
                                    color: "#8A8A93", fontSize: "10px",
                                    textTransform: "uppercase" }}>v1 (production default)</th>
                    <th style={{ textAlign: "left", padding: "8px 10px",
                                    color: "#8A8A93", fontSize: "10px",
                                    textTransform: "uppercase" }}>v2 (shadow)</th>
                  </tr>
                </thead>
                <tbody>
                  <PairRow label="Symbol"     v1={v1?.symbol} v2={v2?.symbol} />
                  <PairRow label="Broker"     v1={v1?.chain_meta?.active_broker}
                                                v2={v2?.chain_meta?.active_broker} />
                  <PairRow label="Expiration" v1={v1?.expiration}
                                                v2={v2?.chosen_expiration || v2?.expiration} />
                  <PairRow label="Spot"       v1={v1?.spot} v2={v2?.spot} />
                  <PairRow label="Quote TS"   v1={v1?.chain_meta?.fetched_at}
                                                v2={v2?.chain_meta?.fetched_at} />
                  <PairRow label="Chain hash" v1={v1?.chain_meta?.chain_hash}
                                                v2={v2?.chain_meta?.chain_hash} />
                  <PairRow label="Engine version" v1={v1?.chain_meta?.engine_version}
                                                     v2={v2?.engine_version} />
                  <PairRow label="Net GEX"    v1={v1?.total_net_gex}
                                                v2={v2?.total_net_gex} />
                  <PairRow label="Regime"     v1={v1?.regime} v2={v2?.regime} />
                  <PairRow label="Zero Gamma" v1={v1?.zero_gamma}
                                                v2={v2?.zero_gamma} />
                  <PairRow label="ZG quality" v1={v1?.zero_gamma_info?.quality_state}
                                                v2={v2?.zero_gamma_info?.quality_state} />
                  <PairRow label="Primary call wall"
                              v1={v1?.walls?.call?.strike ?? v1?.walls?.call}
                              v2={v2?.walls?.primary_call_wall?.center_strike} />
                  <PairRow label="Primary put wall"
                              v1={v1?.walls?.put?.strike ?? v1?.walls?.put}
                              v2={v2?.walls?.primary_put_wall?.center_strike} />
                  <PairRow label="Walls usage status" v1={v1?.walls_status}
                                                        v2={v2?.walls?.usage_status} />
                  <PairRow label="Calc Integrity"
                              v1={v1?.confidence?.calculation_integrity?.score ?? "—"}
                              v2={v2?.confidence?.calculation_integrity?.score} />
                  <PairRow label="Structural Confidence"
                              v1={v1?.confidence?.structural_confidence?.score ?? "—"}
                              v2={v2?.confidence?.structural_confidence?.score} />
                  <PairRow label="WES source" v1={v1?.wes?.source ?? v1?.wes_meta?.source}
                                                v2={v2?.wes_meta?.source} />
                  <PairRow label="WES freshness (s)"
                              v1={v1?.wes?.wes_age_sec ?? v1?.wes_meta?.wes_age_sec}
                              v2={v2?.wes_meta?.wes_age_sec} />
                  <PairRow label="Market State" v1="—"
                                                  v2={v2?.market_state?.state_code} />
                  <PairRow label="Path Status"  v1="—"
                                                  v2={(v2?.path_status?.reason_codes || []).join(", ")} />
                </tbody>
              </table>
            </div>

            {v2?.trade_status_by_objective && (
              <div data-testid="objective-status-panel"
                    style={{ border: "1px solid #26262B", background: "#0A0A0D",
                             borderRadius: "3px", padding: "10px 12px" }}>
                <h3 style={{ margin: "0 0 6px 0", color: "#fff", fontSize: "11px",
                                textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Objective-specific Trade Status (v2 only)
                </h3>
                <table style={{ width: "100%", borderCollapse: "collapse",
                                  fontSize: "11px", color: "#e5e7eb" }}>
                  <tbody>
                    {Object.entries(v2.trade_status_by_objective).map(([obj, ts]) => (
                      <tr key={obj} data-testid={`objective-${obj}`}
                          style={{ borderTop: "1px solid #1A1A20" }}>
                        <td style={{ padding: "4px 8px", color: "#8A8A93",
                                        textTransform: "uppercase", width: "220px" }}>
                          {obj}
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          {ts?.outcome}
                          {ts?.invalidation_condition && (
                            <span style={{ color: "#8A8A93", marginLeft: "8px",
                                              fontFamily: "ui-monospace",
                                              fontSize: "10px" }}>
                              inv: {ts.invalidation_condition}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <DriftTable drift={diag.field_level_drift || []}
                          hashesMatch={diag.chain_hashes_match}
                          comparisonLabel={diag.comparison_label} />

            <div>
              <h3 style={{ margin: "0 0 6px 0", color: "#fff", fontSize: "12px",
                             textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Reason Codes (v2)
              </h3>
              <ReasonCodeList rendered={v2Rendered} showAudit />
              {v2Rendered.length === 0 && (
                <div style={{ color: "#8A8A93", fontSize: "11px",
                                fontFamily: "ui-monospace" }}>
                  No reason codes in this v2 payload.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
